/**
 * HQ Super Admin -> Entity Reset
 *
 * Wipes every transactional record for a chosen Store or Warehouse.
 * Hard-gated by:
 *   - HQ Super Admin role (backend enforces 403)
 *   - User must retype the entity code
 *   - User must re-enter their password
 *
 * Store Reset is all-or-nothing (per the user's choice).
 * Warehouse Reset lets the admin tick which transactional groups to wipe.
 */
import { useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle, Building2, Warehouse as WarehouseIcon, ShieldAlert, History, Loader2,
  CheckCircle2, Trash2, Eye, EyeOff, FileEdit,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import api from '@/services/api';
import { formatDate } from '@/utils/dateFormat';
import { GRNCorrectionTab } from '@/components/hq/GRNCorrectionTab';
import { StockHealthTab } from '@/components/hq/StockHealthTab';

const fmt = (n) => (Number(n) || 0).toLocaleString('en-IN');

// ---------------------------------------------------------------------------
// Preview + Confirm card shared between Store/Warehouse views
// ---------------------------------------------------------------------------
function PreviewPanel({ title, entity, counts, totalLabel, totalValue, extras = [], onConfirm, confirming }) {
  if (!entity) return null;
  const entries = Object.entries(counts || {}).filter(([, v]) => Number(v) > 0);
  return (
    <Card className="border-rose-200">
      <CardHeader className="bg-rose-50">
        <CardTitle className="text-rose-800 text-base flex items-center gap-2">
          <AlertTriangle className="w-5 h-5" />
          {title}
        </CardTitle>
        <CardDescription className="text-rose-700">
          <span className="font-semibold">{entity.name}</span>
          <span className="text-xs ml-2">({entity.code})</span>
        </CardDescription>
      </CardHeader>
      <CardContent className="p-4 space-y-3">
        {entries.length === 0 ? (
          <p className="text-sm text-gray-500">Nothing to wipe. This entity already has zero transactional records.</p>
        ) : (
          <div className="rounded-md border border-gray-200 bg-white">
            <Table>
              <TableHeader className="bg-gray-50">
                <TableRow>
                  <TableHead className="text-xs">Collection</TableHead>
                  <TableHead className="text-xs text-right">Records</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {entries.map(([k, v]) => (
                  <TableRow key={k}>
                    <TableCell className="text-sm font-mono">{k}</TableCell>
                    <TableCell className="text-right font-semibold">{fmt(v)}</TableCell>
                  </TableRow>
                ))}
                <TableRow className="bg-rose-50 font-bold">
                  <TableCell className="text-rose-800">{totalLabel}</TableCell>
                  <TableCell className="text-right text-rose-700">{fmt(totalValue)}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
        )}
        {extras.map((ex, idx) => (
          <div key={idx} className="text-xs text-gray-500 flex items-center gap-2 bg-gray-50 border border-gray-200 rounded px-3 py-2">
            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
            {ex}
          </div>
        ))}
        <Button
          onClick={onConfirm}
          disabled={entries.length === 0 || confirming}
          variant="destructive"
          className="w-full"
          data-testid="open-confirm-reset-btn"
        >
          <Trash2 className="w-4 h-4 mr-2" />
          Reset {entity.name}…
        </Button>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Confirmation dialog that captures entity code + password
// ---------------------------------------------------------------------------
function ConfirmResetDialog({
  open, onOpenChange, entityKind, entity, groupsLabel, onConfirmed, isWarehouse, chosenGroups,
}) {
  const { toast } = useToast();
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) {
      setCode('');
      setPassword('');
      setShowPw(false);
    }
  }, [open]);

  const canSubmit = (code || '').trim().toUpperCase() === (entity?.code || '').toUpperCase()
    && (password || '').length > 0
    && (!isWarehouse || (chosenGroups && chosenGroups.length > 0));

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    try {
      const url = isWarehouse
        ? `/hq/reset/warehouse/${entity.id}`
        : `/hq/reset/store/${entity.id}`;
      const body = isWarehouse
        ? { warehouse_code: code.trim(), password, groups: chosenGroups }
        : { store_code: code.trim(), password };
      const res = await api.post(url, body);
      toast({
        title: 'Reset complete',
        description: `Wiped ${res.data.total_deleted} records from ${entity.name}`,
      });
      onConfirmed?.(res.data);
      onOpenChange(false);
    } catch (err) {
      toast({
        title: 'Reset failed',
        description: err.response?.data?.detail || err.message,
        variant: 'destructive',
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="confirm-reset-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-rose-700">
            <ShieldAlert className="w-5 h-5" />
            Confirm {entityKind} Reset
          </DialogTitle>
          <DialogDescription className="text-gray-600">
            This permanently wipes transactional data for
            <span className="font-semibold text-gray-800"> {entity?.name} ({entity?.code})</span>.
            {groupsLabel ? <><br /><span className="text-xs">Groups: {groupsLabel}</span></> : null}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1">
            <Label className="text-sm">Type the {entityKind.toLowerCase()} code to confirm</Label>
            <Input
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder={entity?.code}
              className="font-mono"
              data-testid="confirm-code-input"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-sm">Re-enter your password</Label>
            <div className="relative">
              <Input
                type={showPw ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Your admin password"
                className="pr-10"
                data-testid="confirm-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPw((v) => !v)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                tabIndex={-1}
              >
                {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded px-3 py-2 text-xs text-amber-800 flex gap-2">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
            This action cannot be undone. The data is permanently removed.
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={submit}
            disabled={!canSubmit || busy}
            data-testid="confirm-reset-submit-btn"
          >
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
            Permanently Wipe
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Store Reset tab
// ---------------------------------------------------------------------------
function StoreResetTab() {
  const { toast } = useToast();
  const [stores, setStores] = useState([]);
  const [storeId, setStoreId] = useState('');
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/stores');
        const items = res.data?.stores || res.data || [];
        setStores(items);
      } catch (err) {
        toast({ title: 'Failed to load stores', variant: 'destructive' });
      }
    })();
  }, [toast]);

  const loadPreview = async (sid) => {
    if (!sid) {
      setPreview(null);
      return;
    }
    setLoading(true);
    try {
      const res = await api.get(`/hq/reset/store/${sid}/preview`);
      setPreview(res.data);
    } catch (err) {
      toast({
        title: 'Preview failed',
        description: err.response?.data?.detail || err.message,
        variant: 'destructive',
      });
      setPreview(null);
    } finally {
      setLoading(false);
    }
  };

  const onSelect = (sid) => {
    setStoreId(sid);
    loadPreview(sid);
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Building2 className="w-5 h-5 text-emerald-600" />
            Pick a store to reset
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Select value={storeId} onValueChange={onSelect}>
            <SelectTrigger className="w-full" data-testid="store-select">
              <SelectValue placeholder="Choose a store…" />
            </SelectTrigger>
            <SelectContent>
              {stores.map((s) => (
                <SelectItem key={s.id} value={s.id}>
                  {s.code} — {s.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {loading && <div className="text-sm text-gray-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />Loading preview…</div>}

      {preview && (
        <PreviewPanel
          title="Everything that will be wiped"
          entity={preview.store}
          counts={preview.counts}
          totalLabel="Total records to delete"
          totalValue={preview.total_records}
          extras={[
            `Customer master kept (${fmt(preview.customers_preserved)} customers registered at this store will remain).`,
            'Store master, users, products, categories and other HQ masters are untouched.',
          ]}
          onConfirm={() => setConfirmOpen(true)}
        />
      )}

      <ConfirmResetDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        entityKind="Store"
        entity={preview?.store}
        onConfirmed={() => loadPreview(storeId)}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Warehouse Reset tab
// ---------------------------------------------------------------------------
function WarehouseResetTab() {
  const { toast } = useToast();
  const [warehouses, setWarehouses] = useState([]);
  const [whId, setWhId] = useState('');
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [chosen, setChosen] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/warehouses');
        const items = res.data?.warehouses || res.data || [];
        setWarehouses(items);
      } catch (err) {
        toast({ title: 'Failed to load warehouses', variant: 'destructive' });
      }
    })();
  }, [toast]);

  const loadPreview = async (id) => {
    if (!id) {
      setPreview(null);
      setChosen([]);
      return;
    }
    setLoading(true);
    try {
      const res = await api.get(`/hq/reset/warehouse/${id}/preview`);
      setPreview(res.data);
      // Default: tick every group that has records
      const auto = Object.entries(res.data.group_totals || {})
        .filter(([, v]) => Number(v) > 0)
        .map(([k]) => k);
      setChosen(auto);
    } catch (err) {
      toast({
        title: 'Preview failed',
        description: err.response?.data?.detail || err.message,
        variant: 'destructive',
      });
      setPreview(null);
    } finally {
      setLoading(false);
    }
  };

  const toggleGroup = (key) => {
    setChosen((prev) => prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]);
  };

  const chosenCounts = useMemo(() => {
    if (!preview) return {};
    const out = {};
    for (const g of chosen) {
      Object.entries(preview.group_counts?.[g] || {}).forEach(([k, v]) => { out[k] = v; });
    }
    return out;
  }, [chosen, preview]);

  const chosenTotal = useMemo(
    () => Object.values(chosenCounts).reduce((s, v) => s + (Number(v) > 0 ? Number(v) : 0), 0),
    [chosenCounts]
  );

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <WarehouseIcon className="w-5 h-5 text-emerald-600" />
            Pick a warehouse to reset
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Select value={whId} onValueChange={(v) => { setWhId(v); loadPreview(v); }}>
            <SelectTrigger className="w-full" data-testid="warehouse-select">
              <SelectValue placeholder="Choose a warehouse…" />
            </SelectTrigger>
            <SelectContent>
              {warehouses.map((w) => (
                <SelectItem key={w.id} value={w.id}>
                  {w.code} — {w.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {loading && <div className="text-sm text-gray-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />Loading preview…</div>}

      {preview && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Pick what to wipe</CardTitle>
            <CardDescription>Only the ticked groups are deleted.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            {Object.entries(preview.groups).map(([key, label]) => {
              const total = preview.group_totals?.[key] || 0;
              return (
                <label
                  key={key}
                  className={`flex items-center justify-between rounded-md border px-3 py-2 cursor-pointer ${
                    total === 0 ? 'opacity-50 cursor-not-allowed' : chosen.includes(key) ? 'bg-rose-50 border-rose-200' : 'bg-white border-gray-200'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <Checkbox
                      checked={chosen.includes(key)}
                      disabled={total === 0}
                      onCheckedChange={() => toggleGroup(key)}
                      data-testid={`wh-group-${key}`}
                    />
                    <span className="font-medium">{label}</span>
                  </div>
                  <Badge variant={total > 0 ? 'destructive' : 'secondary'}>{fmt(total)} records</Badge>
                </label>
              );
            })}
          </CardContent>
        </Card>
      )}

      {preview && chosen.length > 0 && (
        <PreviewPanel
          title="Everything that will be wiped"
          entity={preview.warehouse}
          counts={chosenCounts}
          totalLabel="Total records to delete"
          totalValue={chosenTotal}
          extras={['Warehouse master, vendors, products and HQ masters are untouched.']}
          onConfirm={() => setConfirmOpen(true)}
        />
      )}

      <ConfirmResetDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        entityKind="Warehouse"
        entity={preview?.warehouse}
        isWarehouse
        chosenGroups={chosen}
        groupsLabel={chosen.map((k) => preview?.groups?.[k]).filter(Boolean).join(', ')}
        onConfirmed={() => loadPreview(whId)}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// History tab
// ---------------------------------------------------------------------------
function ResetHistoryTab() {
  const { toast } = useToast();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/hq/reset/logs', { params: { limit: 50 } });
      setLogs(res.data?.logs || []);
    } catch (err) {
      toast({ title: 'Failed to load history', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between">
        <div>
          <CardTitle className="text-base flex items-center gap-2">
            <History className="w-5 h-5 text-gray-600" /> Reset History
          </CardTitle>
          <CardDescription>Recent wipes performed by HQ Super Admins</CardDescription>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading}>
          Refresh
        </Button>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="text-sm text-gray-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />Loading…</div>
        ) : logs.length === 0 ? (
          <p className="text-sm text-gray-500">No resets logged yet.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>When</TableHead>
                <TableHead>Scope</TableHead>
                <TableHead>Entity</TableHead>
                <TableHead>Groups</TableHead>
                <TableHead className="text-right">Records</TableHead>
                <TableHead>By</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {logs.map((l) => (
                <TableRow key={l.id}>
                  <TableCell className="text-xs">{formatDate(l.created_at)}</TableCell>
                  <TableCell>
                    <Badge variant={l.scope === 'store' ? 'default' : 'secondary'}>{l.scope}</Badge>
                  </TableCell>
                  <TableCell className="font-medium">
                    {l.entity_name} <span className="text-xs text-gray-500">({l.entity_code})</span>
                  </TableCell>
                  <TableCell className="text-xs">{(l.groups || l.modules || []).join(', ') || '—'}</TableCell>
                  <TableCell className="text-right font-semibold text-rose-700">{fmt(l.total_deleted)}</TableCell>
                  <TableCell className="text-xs">{l.performed_by}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Page shell
// ---------------------------------------------------------------------------
export default function EntityReset() {
  const [tab, setTab] = useState('store');

  return (
    <div className="p-6 lg:p-8 bg-gray-50 min-h-screen">
      <div className="mb-6 flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <ShieldAlert className="w-6 h-6 text-rose-600" />
            Entity Reset
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Permanently wipe transactional data for a Store or Warehouse, or correct a completed GRN. HQ Super Admin only.
          </p>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="store" data-testid="tab-store">
            <Building2 className="w-4 h-4 mr-2" />
            Store Reset
          </TabsTrigger>
          <TabsTrigger value="warehouse" data-testid="tab-warehouse">
            <WarehouseIcon className="w-4 h-4 mr-2" />
            Warehouse Reset
          </TabsTrigger>
          <TabsTrigger value="grn" data-testid="tab-grn-correction">
            <FileEdit className="w-4 h-4 mr-2" />
            GRN Correction
          </TabsTrigger>
          <TabsTrigger value="stock-health" data-testid="tab-stock-health">
            <FileEdit className="w-4 h-4 mr-2" />
            Stock Health
          </TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">
            <History className="w-4 h-4 mr-2" />
            History
          </TabsTrigger>
        </TabsList>

        <TabsContent value="store" className="mt-4"><StoreResetTab /></TabsContent>
        <TabsContent value="grn" className="mt-4"><GRNCorrectionTab /></TabsContent>
        <TabsContent value="stock-health" className="mt-4"><StockHealthTab /></TabsContent>
        <TabsContent value="warehouse" className="mt-4"><WarehouseResetTab /></TabsContent>
        <TabsContent value="history" className="mt-4"><ResetHistoryTab /></TabsContent>
      </Tabs>

      <Toaster />
    </div>
  );
}
